import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  AnimatePresence,
  animate,
  motion,
  useIsPresent,
  useMotionValue,
  type MotionValue,
  type Transition,
  type Variants,
} from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import {
  HOME_VIEW_ID,
  useViewActions,
  useSetViewOpen,
  useViewHistory,
  useViewNavigation,
  type SidepanelView,
} from '@renderer/overlay/Sidepanel/view-atoms'
import { topEntry, type ViewHistoryEntry } from './view-history'
import { useSidepanelSide } from './atoms'
import { useSwipeDismiss } from './swipe-gesture'
import { SIDEPANEL_SPRING, SIDEPANEL_SNAP_BACK_SPRING, SIDEPANEL_LAYER_Z } from './constants'
import { sidepanelSideSign } from '@shared/sidepanel/side'
import { syncInteractiveRegion } from '@highlight/ui'
import { ViewContext, ViewEntranceSettledContext } from './view-context'
import { viewChrome } from './view-registry'

type ViewEntry = ViewHistoryEntry<SidepanelView>

/**
 * How far a view travels as it swaps. Deliberately small: views slide *within*
 * the panel rather than off its edge, so a navigation reads as the content
 * shifting rather than a card flying across the screen.
 */
const VIEW_SWAP_OFFSET_PX = 28

/**
 * Asymptote for the swipe peek: raw finger travel is resisted onto this budget so
 * a view can never be dragged past the panel's edge. Chosen against the gesture's
 * commit threshold (100px, `swipe-gesture.ts`) — at the distance where the swipe
 * commits, the view has travelled ~28px, the same distance the swap moves it. So
 * the gesture ends exactly where the exit would have started.
 */
const VIEW_PEEK_MAX_PX = 40

/** +1 when navigating forward (push), -1 when going back — drives slide direction. */
type NavDirection = 1 | -1

interface ViewSwapCustom {
  direction: NavDirection
  /** The navigation came from a committed swipe, so the finger already did the travel. */
  gestureCommitted: boolean
}

const viewVariants: Variants = {
  enter: ({ direction }: ViewSwapCustom) => ({ x: direction * VIEW_SWAP_OFFSET_PX, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  // After a committed swipe the view is already sitting at its peek offset, held
  // by the gesture layer. Sliding it again would stack a second, separate motion
  // on top of the one the user just performed — so it only dissolves in place.
  exit: ({ direction, gestureCommitted }: ViewSwapCustom) =>
    gestureCommitted ? { opacity: 0 } : { x: -direction * VIEW_SWAP_OFFSET_PX, opacity: 0 },
}

const swapTransition: Transition = { ...SIDEPANEL_SPRING, opacity: { duration: 0.12, ease: 'easeOut' } }

/**
 * If `onAnimationComplete` never runs (a remount while AnimatePresence still
 * considers the entry present), settle anyway rather than withhold the body
 * forever. Twice the slide so a slow frame does not beat the real callback.
 */
const ENTRANCE_SETTLE_FALLBACK_MS = 400

/**
 * The view router (ADR 0001): renders the top history entry only when it is
 * not the stack root.
 *
 * The router owns all view motion — the direction-aware swap between views and
 * the two-finger swipe — so a View itself is just content on a surface. Home
 * renders outside the router (`SidepanelSections`). A popout window's stack is
 * rooted on the popped View instead, so there the router renders every entry.
 */
export const ViewRouter = memo(() => {
  const history = useViewHistory()
  const { closeView, forwardView } = useViewActions()
  const { canGoForward } = useViewNavigation()
  const setViewOpen = useSetViewOpen()
  const side = useSidepanelSide()
  const sideSign = sidepanelSideSign(side)

  const top = topEntry(history)
  const current = top.view.id === HOME_VIEW_ID ? null : top
  const isViewOpen = current !== null

  // Derived *during render*, not in an effect: AnimatePresence reads `custom`
  // on the same render that mounts the incoming view, so computing this in an
  // effect would leave every transition one navigation behind — and therefore
  // pointing the wrong way.
  const [prevIndex, setPrevIndex] = useState(history.index)
  const [direction, setDirection] = useState<NavDirection>(1)
  if (history.index !== prevIndex) {
    setDirection(history.index > prevIndex ? 1 : -1)
    setPrevIndex(history.index)
  }

  // Tracked so the open flag survives the close animation: the main process
  // locks a swipe's target when the gesture *begins*, and a View still
  // dissolving on screen is what a back swipe should pop. Without this, a swipe
  // started mid-exit would dismiss the whole panel instead.
  const [isViewExiting, setIsViewExiting] = useState(false)
  const [gestureCommitted, setGestureCommitted] = useState(false)
  const [prevViewOpen, setPrevViewOpen] = useState(isViewOpen)
  if (isViewOpen !== prevViewOpen) {
    setPrevViewOpen(isViewOpen)
    // Both flags clear on open as well as via `onExitComplete`: navigating
    // forward before the close animation finished resurrects the exiting entry,
    // so that callback never fires. Left latched, `isViewExiting` keeps the open
    // flag true forever — the main process then locks every later swipe to the
    // `view` target and this router drops it, so the panel stops responding.
    setIsViewExiting(!isViewOpen)
    if (isViewOpen) setGestureCommitted(false)
  }

  useLayoutEffect(() => {
    setViewOpen(isViewOpen || isViewExiting)
  }, [isViewOpen, isViewExiting, setViewOpen])

  // The swipe drives the *current entry's own* transform, not a shared layer
  // above both views. That's what makes the gesture and the swap one motion: the
  // outgoing view keeps the offset the finger left it at and unmounts with it, so
  // there is never an offset to undo — and an undo is a second, opposing slide.
  const topGestureX = useRef<MotionValue<number> | null>(null)
  const registerGestureX = useCallback((gestureX: MotionValue<number>) => {
    topGestureX.current = gestureX
    return () => {
      // An exiting entry unregisters only when its exit finishes, which is *after*
      // the incoming entry has claimed the slot. Without this guard it would clear
      // the new registration and the next swipe would move nothing.
      if (topGestureX.current === gestureX) {
        topGestureX.current = null
      }
    }
  }, [])

  useSwipeDismiss({
    // A View on screen owns the gesture; Home's handler takes it otherwise.
    // Two mutually exclusive claims over the same flag, so exactly one of them
    // answers every swipe.
    claims: () => isViewOpen,
    sideSign,
    // Only when there's somewhere to go: a forward swipe peeks the view away
    // from the edge so the gesture answers the finger in both directions.
    allowForwardTravel: () => canGoForward,
    onMove: (offset) => {
      // Resistance, not a hard clamp: travel approaches VIEW_PEEK_MAX_PX and never
      // reaches it, so the view peeks within the panel however far the finger goes.
      const magnitude = Math.abs(offset)
      const resisted = (VIEW_PEEK_MAX_PX * magnitude) / (magnitude + VIEW_PEEK_MAX_PX)
      topGestureX.current?.set(Math.sign(offset) * resisted)
    },
    onEnd: ({ shouldClose, shouldForward }) => {
      // A leave guard (unsaved edits) can defer the navigation, reported by the
      // `false` return: the view stays, so the peek snaps back like an
      // uncommitted swipe and the guard's dialog takes it from there. A
      // confirmed leave then runs the ordinary swap, not the gesture exit.
      if (shouldClose && closeView()) {
        // Deliberately no reset: the outgoing view holds its peek offset while it
        // dissolves, continuing the gesture rather than reversing it.
        setGestureCommitted(true)
      } else if (shouldForward && canGoForward && forwardView()) {
        // Same follow-through as a close: the outgoing view dissolves from the
        // offset the finger left it at instead of sliding back first.
        setGestureCommitted(true)
      } else {
        const gestureX = topGestureX.current
        if (gestureX) {
          animate(gestureX, 0, SIDEPANEL_SNAP_BACK_SPRING)
        }
      }
    },
  })

  const swapCustom = useMemo<ViewSwapCustom>(() => ({ direction, gestureCommitted }), [direction, gestureCommitted])

  // A swap's outgoing view lands here too, with the incoming one already on
  // screen — clearing the exit flag then is harmless, since `isViewOpen` is
  // still true and the open flag is the union of the two.
  const handleExitComplete = useCallback(() => {
    setGestureCommitted(false)
    setIsViewExiting(false)
  }, [])

  // Deliberately no early return when Home is on top: unmounting the
  // AnimatePresence would drop the outgoing view instantly, so navigating back
  // to Home would have no exit animation. pointer-events-none keeps the empty
  // full-band container from swallowing Home clicks.
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: SIDEPANEL_LAYER_Z.view }}>
      {/* `custom` here (not just on the child) is what an exiting view resolves
          its variant against — the child's own props are frozen at removal. */}
      <AnimatePresence custom={swapCustom} onExitComplete={handleExitComplete}>
        {current && (
          <RoutedView key={current.key} entry={current} custom={swapCustom} registerGestureX={registerGestureX} />
        )}
      </AnimatePresence>
    </div>
  )
})

ViewRouter.displayName = 'ViewRouter'

interface RoutedViewProps {
  entry: ViewEntry
  custom: ViewSwapCustom
  /** Claims this entry as the swipe target; returns its unregister. */
  registerGestureX: (gestureX: MotionValue<number>) => () => void
}

const RoutedView = memo(({ entry, custom, registerGestureX }: RoutedViewProps) => {
  const ViewComponent = viewChrome(entry.view)?.render
  // Both views are in the DOM mid-swap; only the incoming one is "top", so
  // hotkeys that scope to the open view don't target the outgoing one.
  const isTop = useIsPresent()
  // A view that opted out of the transition has nothing to wait for, so it
  // starts settled — `onAnimationComplete` only fires when an animation runs.
  const skipEntrance = entry.view.skipEntrance ?? false
  const [entranceSettled, setEntranceSettled] = useState(skipEntrance)
  const settleEntrance = useCallback(() => setEntranceSettled(true), [])

  useEffect(() => {
    if (entranceSettled) {
      return
    }
    const timeout = window.setTimeout(settleEntrance, ENTRANCE_SETTLE_FALLBACK_MS)
    return () => window.clearTimeout(timeout)
  }, [entranceSettled, settleEntrance])

  // Owned per entry rather than shared by the router, so an exiting view simply
  // keeps wherever the swipe left it instead of being animated back to zero.
  const gestureX = useMotionValue(0)
  useLayoutEffect(() => registerGestureX(gestureX), [registerGestureX, gestureX])

  // Navigating back to an entry whose exit is still running re-presents the
  // *same* instance (history keys are stable), and it still carries the offset
  // the swipe left it holding — the exit deliberately never resets it. Nothing
  // else would ever clear that, so the view would sit mid-swipe for good.
  // Re-entering means the gesture is over, so it starts from the origin again.
  useLayoutEffect(() => {
    if (isTop) {
      gestureX.jump(0)
    }
  }, [isTop, gestureX])

  // Views register no tracker of their own (the panel band's region already
  // covers the view rect), so taking the top slot moves no bounds and the
  // measurement loop publishes nothing. Main would keep a stale click-through
  // verdict until the first mousemove, letting multi-touch gestures fall
  // through to the window behind. Force a frame so main re-hit-tests the
  // stationary cursor at mount, before any pointer event arrives.
  useLayoutEffect(() => {
    if (isTop) {
      syncInteractiveRegion()
    }
  }, [isTop])

  const contextValue = useMemo(
    () => ({
      id: entry.view.id,
      context: entry.view.context,
      entryKey: entry.key,
      sourceLabel: entry.view.sourceLabel,
    }),
    [entry],
  )
  const viewElement = useMemo(() => (ViewComponent ? <ViewComponent /> : null), [ViewComponent])

  if (!ViewComponent) {
    console.warn('Unsupported view id:', entry.view.id)
    return null
  }

  return (
    // Gesture and swap need separate elements: a `style` motion value and a
    // variant can't both own `x`. This layer holds only the swipe offset.
    <motion.div className="absolute inset-0" style={{ x: gestureX, willChange: 'transform' }}>
      <motion.div
        // The panel shell is pointer-events-none so blank areas stay
        // click-through; each interactive region re-enables itself. The outgoing
        // view stays inert so it can't swallow clicks mid-swap.
        className={twMerge('absolute inset-0', isTop ? 'pointer-events-auto' : 'pointer-events-none')}
        data-view-top={isTop ? 'true' : undefined}
        // Which view this is, not merely that it's on top. `data-view-top`
        // alone forces anything outside React — UI automation, the smoke
        // harness — to infer the open view from a structural fingerprint
        // (search placeholders, cell counts), which collides for views that
        // have neither and silently rots when copy changes. Paired with
        // `data-view-top` so the two are read together.
        data-view-id={entry.view.id}
        custom={custom}
        variants={viewVariants}
        initial={skipEntrance ? false : 'enter'}
        animate="center"
        exit="exit"
        transition={swapTransition}
        onAnimationComplete={settleEntrance}
      >
        <ViewContext.Provider value={contextValue}>
          <ViewEntranceSettledContext.Provider value={entranceSettled}>
            {viewElement}
          </ViewEntranceSettledContext.Provider>
        </ViewContext.Provider>
      </motion.div>
    </motion.div>
  )
})

RoutedView.displayName = 'RoutedView'
